import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as api from '@codegrade/api';
import * as icons from '@codegrade/icons';
import { assert, expr, maybe, string } from '@codegrade/utils';
import { Icon } from '@/components/Icon';
import User from '@/components/User';
import { AssignmentsStore, NotificationStore, SubmissionsStore } from '@/store';
import * as utils from '@/utils';
import AbbreviatedTime from '../AbbreviatedTime';
export default tsx.componentFactoryOf().create({
    name: 'user-notification',
    props: {
        /** The notification to be rendered. */
        notification: p.ofType().required,
        /** Whether the notification has been selected. */
        checked: p(Boolean).default(false),
    },
    computed: {
        /** @returns A Maybe assignment. */
        assignment() {
            AssignmentsStore.loadSingleAssignment({ assignmentId: this.notification.assignmentId });
            return AssignmentsStore.getAssignment()(this.notification.assignmentId, {});
        },
        /** @returns A Maybe submission. */
        submission() {
            return SubmissionsStore.getSingleSubmission()(this.notification.workId);
        },
        /** @returns The feedback type given. */
        feedbackType() {
            return expr.match(this.notification, 'type', {
                general_comment_notification: () => 'a general',
                comment_notification: () => 'an inline',
            });
        },
        /** @returns Either null or the location (URL).  */
        notificationRoute() {
            const notification = this.notification;
            const baseRoute = {
                params: {
                    courseId: notification.courseId.toString(10),
                    assignmentId: notification.assignmentId.toString(10),
                    submissionId: notification.workId.toString(10),
                },
                query: {
                    replyToFocus: `${notification.commentBaseId}-${notification.commentReply.id}`,
                },
            };
            return expr.match(notification, 'type', {
                general_comment_notification: () => ({
                    ...baseRoute,
                    name: 'submission',
                    hash: '#feedback-overview',
                }),
                comment_notification: ({ fileId }) => ({
                    ...baseRoute,
                    name: 'submission_file',
                    params: {
                        ...baseRoute.params,
                        fileId: fileId.toString(),
                    },
                    hash: '#code',
                }),
            });
        },
        /** @returns The status of the individual notification. */
        readStatusNode() {
            // Here the `min-w-6` is keeping this from collapsing on smaller screens.
            return utils.ifExpr(this.notification.read, () => <td class="w-6 text-center align-middle !px-0"/>, () => (<td class="min-w-6 w-6 text-center align-middle !px-0">
                        <Icon icon={icons.CircleDot} filled={true} scale={0.5} class="text-positive-40"/>
                    </td>));
        },
        /** @returns The checkbox node. */
        checkboxNode() {
            const notificationId = `notification-checkbox-${this.notification.id}`;
            const assignmentName = this.assignment.map(a => a.name).orDefault('unknown assignment');
            return (<td class="w-4 !pl-1 align-middle" aria-label={`Select ${this.feedbackType} feedback notification for ${assignmentName}`}>
                    <input id={notificationId} type="checkbox" class="size-4 align-middle" disabled={this.notification.read} checked={this.checked} aria-label={`Select notification about ${this.feedbackType} feedback`} onInput={(event) => {
                    const target = event.target;
                    tsx.emitOn(this, 'onToggleCheckbox', {
                        notificationId: this.notification.id,
                        checked: target.checked,
                    });
                }}/>
                </td>);
        },
        /** @returns The content node for the notification. */
        contentNode() {
            const url = this.$router.resolve(this.notificationRoute).href;
            return (<td class="flex">
                    <a href={url} onClick={(e) => {
                    e.preventDefault();
                    this.handleRowClick(e);
                }} onKeydown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        this.handleRowClick(e);
                    }
                }}>
                        Submission by {this.userNode} for {this.assignmentNameNode}
                    </a>
                </td>);
        },
        /** @returns The title of the notification. */
        userNode() {
            return maybe.and(this.assignment, this.submission).caseOf({
                Nothing: () => <span class="italic">unknown</span>,
                Just: ([assignment, submission]) => (<User user={submission.user} anonymize-for={assignment} showWhenYou="you"/>),
            });
        },
        /** @returns The assignment name related to the notification. */
        assignmentNameNode() {
            return this.assignment.caseOf({
                Nothing: () => utils.ELLIPSIS,
                Just: assignment => <div class="contents">"{assignment.name}"</div>,
            });
        },
        /** @returns A node showing the author of the notification. */
        authorNode() {
            const reply = this.notification.commentReply;
            if (reply instanceof api.ExtendedNonDeletedCommentReply) {
                return (<template>
                        {reply.author.caseOf({
                        Nothing: () => 'Someone',
                        Just: author => (<User user={author} anonymize-for={this.assignment.unsafeCoerce()}/>),
                    })}
                        commented on {this.feedbackType} feedback thread you are following.
                    </template>);
            }
            if (reply instanceof api.DeletedCommentReply) {
                return `A comment was deleted on ${this.feedbackType} feedback thread you are following.`;
            }
            throw assert.AssertionError.assertNever(reply);
        },
        /** @returns All badges for the notification. */
        badgesNode() {
            return (<td class="badge-wrapper shrink gap-2">
                    {this.notification.reasons.map(([reason, explanation]) => (<b-badge key={reason} class="px-2 py-1 mr-1" title={string.capitalize(explanation)}>
                            {reason}
                        </b-badge>))}
                </td>);
        },
        /** @returns The time/date node. */
        timeNode() {
            return (<td class="shrink text-sm text-right align-middle !pr-5">
                    <AbbreviatedTime date={this.notification.createdAt} class="time-display"/>
                </td>);
        },
    },
    methods: {
        /** Handles navigation to the notification, loading required data and marking as read. */
        async handleRowClick(event) {
            const location = this.notificationRoute;
            utils.loadBackgroundPromises(NotificationStore.markAsRead({ notification: this.notification }));
            this.$root.$emit('cg::sidebar::close');
            // If we open in a separate window we let the parent component
            // handle the update, but otherwise we need to do here.
            if (event != null && (event.ctrlKey || event.metaKey)) {
                if (this.checked) {
                    tsx.emitOn(this, 'onToggleCheckbox', {
                        notificationId: this.notification.id,
                        checked: false,
                    });
                }
                const url = this.$router.resolve(location).href;
                window.open(url, '_blank');
            }
            else {
                await this.$router.push(location);
            }
        },
    },
    /** @returns The rendered notification. */
    render() {
        const ariaLabel = `Notification: ${this.feedbackType} feedback on ${this.assignment
            .map(a => a.name)
            .orDefault('assignment')} - ${this.notification.read ? 'read' : 'unread'}`;
        return (<tr class={{
                'user-notification': true,
                read: this.notification.read,
                'cursor-default': true,
            }} aria-label={ariaLabel}>
                {this.readStatusNode}
                {this.checkboxNode}
                {this.contentNode}
                {this.badgesNode}
                {this.timeNode}
            </tr>);
    },
});
